import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Phone, Shield, Clock, MapPin, Zap, Award, Users } from 'lucide-react';
import { PHONES, CORPORATE } from '../content';
import PageHero from '../components/PageHero';
import FinalCTA from '../components/FinalCTA';
import { SEOHead } from '../components/SEOHead';

const VALUES = [
  {
    icon: Zap, title: 'Hız',
    desc: 'Ortalama 30 dakikada müdahale. Vakit kaybetmezsiniz.',
    color: 'var(--red)', bg: 'rgba(229,51,51,.12)', borderColor: 'rgba(229,51,51,.25)',
  },
  {
    icon: Shield, title: 'Güven',
    desc: '24 ay garanti. Sertifikalı ekip. Profesyonel hizmet.',
    color: 'var(--blue)', bg: 'var(--blue-dim)', borderColor: 'var(--blue-border)',
  },
  {
    icon: Award, title: 'Kalite',
    desc: 'Premium markalar. Orijinal parça. Her araç tipine uygun.',
    color: 'var(--green)', bg: 'var(--green-dim)', borderColor: 'var(--green-border)',
  },
];

const METRICS = [
  { icon: Clock, val: '7/24', label: 'Kesintisiz Hizmet', color: 'var(--red)' },
  { icon: MapPin, val: '12+', label: 'İlçe Hizmeti', color: 'var(--blue)' },
  { icon: Users, val: '2017', label: 'Kuruluş Yılı', color: 'var(--green)' },
];

export function Hakkimizda() {
  return (
    <main data-testid="hakkimizda-page" style={{ background: 'var(--bg)' }}>
      <SEOHead path="/hakkimizda" />
      <PageHero
        breadcrumb="Hakkımızda"
        headline="7/24 Lastik."
        accentText="Kocaeli'nin Güvencesi."
        sub="2017'den beri, her gece, her gündüz, yolda kalanların yanındayız."
        badge="2017'den Beri Aktif"
      />

      {/* ─── METRICS BAR ─── */}
      <section style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div className="wrap">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }} className="corp-metrics">
            {METRICS.map(({ icon: Icon, val, label, color }, i) => (
              <div key={label} style={{
                padding: '2rem 1.5rem',
                borderRight: i < 2 ? '1px solid var(--border)' : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem',
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: `${color}18`, border: `1px solid ${color}35`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: color, flexShrink: 0,
                }}>
                  <Icon size={20} strokeWidth={1.75} />
                </div>
                <div>
                  <div style={{
                    fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
                    fontSize: '1.75rem', color: 'var(--text)', lineHeight: 1, letterSpacing: '-.03em',
                  }}>{val}</div>
                  <div style={{ fontSize: '.78rem', color: 'var(--text2)', marginTop: '.15rem' }}>{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ABOUT CONTENT ─── */}
      <section className="section" style={{ background: 'var(--bg)' }}>
        <div className="wrap">
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: .5 }}>
              <p style={{ color: 'var(--text2)', lineHeight: 1.85, fontSize: '1rem', marginBottom: '1.25rem' }}>
                {CORPORATE.about}
              </p>
              <p style={{ color: 'var(--text2)', lineHeight: 1.85, fontSize: '.93rem', marginBottom: '2.5rem' }}>
                Araç-marka fark etmeksizin tüm lastik modellerinin sorunlarına tamir servis hizmetimiz gün ve gecenin her saatinde mevcuttur. Kamyon, minibüs, otobüs, ticari araç, traktör, motosiklet ve bisiklet lastikleri dahil tüm araç tiplerine hizmet veriyoruz.
              </p>

              {/* Values */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.25rem', marginBottom: '2.5rem' }} className="values-grid">
                {VALUES.map(({ icon: Icon, title, desc, color, bg, borderColor }, i) => (
                  <motion.div
                    key={title}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: .4, delay: i * .1 }}
                    style={{
                      background: 'var(--card)', border: '1px solid var(--border)',
                      borderRadius: 'var(--rl)', padding: '1.75rem',
                      textAlign: 'center', transition: 'border-color .2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = borderColor}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  >
                    <div style={{
                      width: 48, height: 48, borderRadius: 12,
                      background: bg, border: `1px solid ${borderColor}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: color, margin: '0 auto .875rem',
                    }}>
                      <Icon size={22} strokeWidth={1.75} />
                    </div>
                    <h3 style={{
                      fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
                      fontSize: '1.05rem', color: 'var(--text)', marginBottom: '.4rem',
                    }}>{title}</h3>
                    <p style={{ fontSize: '.82rem', color: 'var(--text2)', lineHeight: 1.6 }}>{desc}</p>
                  </motion.div>
                ))}
              </div>

              <a href={PHONES.main_tel}
                className="btn btn-primary btn-lg ring num"
                style={{ gap: '.625rem' }}>
                <Phone size={17} /> Bizi Tanıyın — {PHONES.main}
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <FinalCTA headline="Bizi Tanıyın." sub="Bizi Arayın." />

      <style>{`
        @media(max-width:768px){
          .values-grid{grid-template-columns:1fr!important}
          .corp-metrics{grid-template-columns:1fr!important}
          .corp-metrics>div{border-right:none!important;border-bottom:1px solid var(--border)}
          .corp-metrics>div:last-child{border-bottom:none}
        }
      `}</style>
    </main>
  );
}

export function Vizyonumuz() {
  return (
    <CorporatePage
      slug="vizyonumuz"
      label="Vizyonumuz"
      icon={Eye}
      headline="Vizyonumuz."
      accentText="İleriyi Görüyoruz."
      content={CORPORATE.vision}
      badge="Stratejik Hedef"
    />
  );
}

export function Misyonumuz() {
  return (
    <CorporatePage
      slug="misyonumuz"
      label="Misyonumuz"
      icon={Target}
      headline="Misyonumuz."
      accentText="Her Yolda Yanınızdayız."
      content={CORPORATE.mission}
      badge="Temel Değerimiz"
    />
  );
}

function CorporatePage({ slug, label, icon: Icon, headline, accentText, content, badge }) {
  return (
    <main data-testid={`${slug}-page`} style={{ background: 'var(--bg)' }}>
      <PageHero
        breadcrumb={label}
        headline={headline}
        accentText={accentText}
        badge={badge}
      />

      <section className="section" style={{ background: 'var(--bg)' }}>
        <div className="wrap">
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: .5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.75rem' }}>
                <div style={{
                  width: 52, height: 52, borderRadius: 14,
                  background: 'var(--blue-dim)', border: '1px solid var(--blue-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--blue)', flexShrink: 0,
                }}>
                  <Icon size={24} />
                </div>
                <h2 style={{
                  fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
                  fontSize: '1.5rem', color: 'var(--text)',
                }}>{label}</h2>
              </div>

              <p style={{ color: 'var(--text2)', lineHeight: 1.85, fontSize: '1rem', marginBottom: '2rem' }}>
                {content}
              </p>

              <a href={PHONES.main_tel}
                className="btn btn-primary btn-lg ring num"
                style={{ gap: '.625rem' }}>
                <Phone size={17} /> {PHONES.main}
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}
